import type { DiagramNode } from '@civil/schema';
import type { ProjectBundle } from '../project.js';
import { footprint, type LoadedDiagrams } from './altitude.js';

/**
 * The working set (docs/altitude.md §9): entered from the graph, the editor opens on
 * the files that work together on the subject, not the folder it happens to sit in.
 * PRD §2's code node is the floor every descent lands on; a flow step that points at
 * code is released into the editor the same way, on these.
 *
 * Each file is listed under why it is there, nearest first, and a file is listed once,
 * under the first reason that names it. Only what Civil has already read is used:
 * the diagrams' evidence, and the files the lift found for each part.
 */

export type FileReason = 'says' | 'carries' | 'builds' | 'works-with';

export interface FileGroup {
  reason: FileReason;
  title: string;
  files: string[];
}

export const FILE_REASONS: Record<FileReason, string> = {
  says: 'Says it',
  carries: 'Carries it',
  builds: 'Builds it',
  'works-with': 'Works with it',
};

/** Where a working set is read from: a flow view's diagrams, and what the lift found. */
export interface CodeSources {
  loaded: LoadedDiagrams;
  /** The repository's files: evidence naming one that is gone is left out. */
  files: readonly string[];
  /** Part id → the files it stands for (civil/registry.yaml). */
  units?: Record<string, { files: string[] }>;
  /** The composition's edges, for what a part works with. */
  edges?: readonly { from: { node: string }; to: { node: string } }[];
}

/** A group is kept short: past this, a list stops being read. */
const PER_GROUP = 40;

/** "src/a.ts:12" and "src/a.ts:12-30" name the file "src/a.ts". */
export const pathOf = (evidence: string): string => evidence.replace(/:\d+(?:-\d+)?$/, '');

const evidenceOf = (nodes: DiagramNode[]): string[] =>
  nodes.flatMap((n) => [...n.evidence, ...n.decisions.flatMap((d) => d.evidence)]).map(pathOf);

export function workingSet(sources: CodeSources, diagram: string, ids: readonly string[]): FileGroup[] {
  const doc = sources.loaded.diagrams?.[diagram]?.doc;
  if (!doc) return [];
  const here = doc.spec.nodes.filter((n) => ids.includes(n.id));
  const altitude = doc.spec.altitude;
  // What carries a moment or a section: everything its footprint reaches further down.
  const below = (altitude === 'story' ? (['product', 'mechanism'] as const) : altitude === 'product' ? (['mechanism'] as const) : [])
    .flatMap((to) => footprint(sources.loaded, diagram, ids, to).all.map((p) => p.node));

  const parts = new Set([...here, ...below].flatMap((n) => n.realizedBy));
  const neighbours = new Set<string>();
  for (const e of sources.edges ?? []) {
    if (parts.has(e.from.node) && !parts.has(e.to.node)) neighbours.add(e.to.node);
    if (parts.has(e.to.node) && !parts.has(e.from.node)) neighbours.add(e.from.node);
  }
  const filesOf = (ids: Set<string>) => [...ids].sort().flatMap((id) => sources.units?.[id]?.files ?? []);

  const repo = new Set(sources.files);
  const seen = new Set<string>();
  const group = (reason: FileReason, files: string[]): FileGroup[] => {
    const kept: string[] = [];
    for (const f of files) {
      if (kept.length >= PER_GROUP) break;
      if (!repo.has(f) || seen.has(f)) continue;
      seen.add(f);
      kept.push(f);
    }
    return kept.length ? [{ reason, title: FILE_REASONS[reason], files: kept }] : [];
  };
  return [
    ...group('says', evidenceOf(here)),
    ...group('carries', evidenceOf(below)),
    ...group('builds', filesOf(parts)),
    ...group('works-with', filesOf(neighbours)),
  ];
}

/** "ts, py": the languages a set of files is written in, most files first. */
export function languagesOf(files: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const f of files) {
    const ext = /\.([a-z0-9]+)$/i.exec(f)?.[1]?.toLowerCase();
    if (ext) counts.set(ext, (counts.get(ext) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 3).map(([ext]) => ext).join(', ');
}

/** A view's sources, from the bundle the canvas draws. */
export function codeSources(bundle: ProjectBundle, view: string | undefined): CodeSources | undefined {
  const loaded = view ? bundle.views?.[view] : undefined;
  if (!loaded) return undefined;
  return {
    loaded,
    files: bundle.files,
    ...(bundle.lift ? { units: bundle.lift.units } : {}),
    ...(bundle.composition ? { edges: bundle.composition.spec.edges } : {}),
  };
}
